import { Spinner } from '@chakra-ui/react';
import { Box } from '@components/ui/atoms/Box';
import { Stack } from '@components/ui/atoms/Stack';
import { Text } from '@components/ui/atoms/Text';
import { OrganizationType } from '@graphql/enums';
import { useOrganizationType } from '@shared/hooks/useOrganizationType';
import React from 'react';

type PlatformOnlyGuardProps = {
  fallback?: React.ReactNode;
  children: React.ReactElement;
};

/**
 * Restricts a route to users whose organization is the platform itself.
 * Permissions alone are not enough for platform-wide administration pages:
 * a bank or merchant admin can hold organization.* permissions for their
 * own organization without being allowed to manage every bank.
 */
export const PlatformOnlyGuard: React.FC<PlatformOnlyGuardProps> = ({ fallback, children }) => {
  const { orgType, loading } = useOrganizationType();

  if (loading) {
    return (
      <Box w="full" h="full" display="flex" alignItems="center" justifyContent="center" p="12">
        <Spinner />
      </Box>
    );
  }

  if (orgType !== OrganizationType.Platform) {
    return (fallback as React.ReactElement) ?? <PlatformOnly />;
  }

  return children;
};

const PlatformOnly: React.FC = () => (
  <Box
    display="flex"
    alignItems="center"
    justifyContent="center"
    w="full"
    h="full"
    p="8"
    textAlign="center"
  >
    <Stack gap="3" maxW="480px">
      <Text fontSize="lg" fontWeight="600">
        This page is only available to platform administrators.
      </Text>
      <Text color="body.dark" fontSize="md">
        If you think this is a mistake, please contact your administrator to request access.
      </Text>
    </Stack>
  </Box>
);
